import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import type { ChartImportReport } from './accounts.api';
import './accounts.css';

export interface AccountImportDialogProps {
  open: boolean;
  report: ChartImportReport | null;
  fileName?: string;
  error?: string | null;
  busy?: boolean;
  onClose: () => void;
  onApply: (file: File) => void;
  onCheck?: (file: File) => void;
}

const ACTION_KEYS: Record<ChartImportReport['entries'][number]['action'], string> = {
  create: 'accounts.actionCreate',
  update: 'accounts.actionUpdate',
  unchanged: 'accounts.actionUnchanged',
};

/**
 * Dry run first: the diff is shown, the write happens only on the explicit
 * apply button. Per-row failures carry the sheet row number.
 */
export function AccountImportDialog({
  open,
  report,
  fileName,
  error,
  busy,
  onClose,
  onApply,
  onCheck,
}: AccountImportDialogProps): JSX.Element | null {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setCheckError(null);
    }
  }, [open]);

  if (!open) return null;

  const pick = (candidate: File | null): void => {
    setFile(candidate);
    setCheckError(null);
  };

  const runCheck = (): void => {
    if (file === null) {
      setCheckError(t('accounts.fileRequired'));
      return;
    }
    (onCheck ?? onApply)(file);
  };

  return (
    <div className="accounts__overlay">
      <section className="accounts__dialog" role="dialog" aria-modal="true" data-testid="import-dialog">
        <header className="accounts__dialog-head">
          <h2>{t('accounts.importTitle')}</h2>
          <button type="button" className="btn btn--small" onClick={onClose}>
            {t('accounts.close')}
          </button>
        </header>

        <p className="muted">{t('accounts.importHint')}</p>
        <input
          className="input"
          type="file"
          accept=".csv,.xlsx"
          aria-label={t('accounts.chooseFile')}
          onChange={(event) => pick(event.currentTarget.files?.[0] ?? null)}
        />
        {fileName === undefined ? null : <p className="mono">{fileName}</p>}

        {error === null || error === undefined ? null : (
          <p className="alert alert--danger" role="alert">
            {error}
          </p>
        )}
        {checkError === null ? null : (
          <p className="alert alert--danger" role="alert">
            {checkError}
          </p>
        )}

        <div className="accounts__dialog-actions">
          <button type="button" className="btn" disabled={busy === true} onClick={runCheck}>
            {t('accounts.check')}
          </button>
        </div>

        {report === null ? null : (
          <div className="accounts__diff" data-testid="import-diff" data-dry-run={report.dryRun ? 'true' : 'false'}>
            <p className="muted">
              {t('accounts.summaryCreate')}: <strong>{String(report.summary.create)}</strong> ·{' '}
              {t('accounts.summaryUpdate')}: <strong>{String(report.summary.update)}</strong> ·{' '}
              {t('accounts.summaryUnchanged')}: <strong>{String(report.summary.unchanged)}</strong> ·{' '}
              {t('accounts.summaryErrors')}: <strong>{String(report.summary.errors)}</strong>
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('accounts.row')}</th>
                  <th>{t('accounts.code')}</th>
                  <th>{t('accounts.action')}</th>
                  <th>{t('accounts.changes')}</th>
                </tr>
              </thead>
              <tbody>
                {report.entries.map((entry) => (
                  <tr key={`${String(entry.row)}-${entry.code}`} data-testid="import-entry" data-row={entry.row}>
                    <td className="mono">{String(entry.row)}</td>
                    <td className="mono">{entry.code}</td>
                    <td>{t(ACTION_KEYS[entry.action])}</td>
                    <td>{entry.changes.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {report.errors.length === 0 ? null : (
              <ul className="accounts__errors">
                {report.errors.map((rowError) => (
                  <li
                    key={`${String(rowError.row)}-${rowError.column}`}
                    data-testid="import-row-error"
                    data-row={rowError.row}
                    className="alert alert--danger"
                  >
                    {t('accounts.row')} {String(rowError.row)} · <span className="mono">{rowError.column}</span> ·{' '}
                    {rowError.message}
                  </li>
                ))}
              </ul>
            )}

            <div className="accounts__dialog-actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy === true || report.errors.length > 0 || file === null}
                onClick={() => {
                  if (file !== null) onApply(file);
                }}
              >
                {t('accounts.applyImport')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
